import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const DeepSpaceBackground = ({ className = '', showRendererError = false }) => {
    const canvasContainerRef = useRef(null);
    const particleSystemRef = useRef(null);
    const [rendererError, setRendererError] = useState(null);

    useEffect(() => {
        if (!canvasContainerRef.current) return undefined;
        if (process.env.NODE_ENV === 'test') {
            setRendererError('Three.js disabled');
            return undefined;
        }

        class ParticleSystem {
            constructor(container) {
                this.container = container;
                this.scene = null;
                this.camera = null;
                this.renderer = null;
                this.particles = null;
                this.material = null;
                this.time = 0;
                this.animationId = null;

                this.init();
            }

            init() {
                const width = window.innerWidth;
                const height = window.innerHeight;

                this.scene = new THREE.Scene();
                this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
                this.camera.position.z = 50;

                try {
                    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'high-performance' });
                    this.renderer.setSize(width, height);
                    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
                    this.renderer.setClearColor(0x000000, 0);
                } catch (e) {
                    setRendererError('Three.js disabled');
                    return;
                }

                this.container.innerHTML = '';
                this.container.appendChild(this.renderer.domElement);

                const particleCount = 480;
                const positions = new Float32Array(particleCount * 3);
                const attributes = new Float32Array(particleCount * 3);

                for (let i = 0; i < particleCount; i += 1) {
                    positions[i * 3] = (Math.random() - 0.5) * 200;
                    positions[i * 3 + 1] = (Math.random() - 0.5) * 200;

                    const layer = Math.random();
                    if (layer < 0.5) {
                        positions[i * 3 + 2] = -20 - Math.random() * 30;
                        attributes[i * 3] = 0.2 + Math.random() * 0.8;
                        attributes[i * 3 + 1] = 0.05 + Math.random() * 0.05;
                        attributes[i * 3 + 2] = 0.02 + Math.random() * 0.02;
                    } else {
                        positions[i * 3 + 2] = Math.random() * 20;
                        attributes[i * 3] = 1.0 + Math.random() * 1.0;
                        attributes[i * 3 + 1] = 0.05 + Math.random() * 0.05;
                        attributes[i * 3 + 2] = 0.04 + Math.random() * 0.03;
                    }
                }

                const geometry = new THREE.BufferGeometry();
                geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                geometry.setAttribute('aSettings', new THREE.BufferAttribute(attributes, 3));

                const vertexShader = `
                    attribute vec3 aSettings;
                    uniform float time;
                    varying float vOpacity;

                    void main() {
                        vec3 pos = position;

                        float orbitSpeed = time * 0.15 * aSettings.z;
                        float c = cos(orbitSpeed);
                        float s = sin(orbitSpeed);
                        mat2 rot = mat2(c, -s, s, c);
                        pos.xy = rot * pos.xy;

                        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                        gl_Position = projectionMatrix * mvPosition;
                        gl_PointSize = aSettings.x * (300.0 / -mvPosition.z);
                        vOpacity = aSettings.y;
                    }
                `;

                const fragmentShader = `
                    varying float vOpacity;

                    void main() {
                        vec2 coord = gl_PointCoord - vec2(0.5);
                        float dist = length(coord);
                        if (dist > 0.5) discard;

                        float alpha = smoothstep(0.5, 0.1, dist) * vOpacity;
                        gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
                    }
                `;

                this.material = new THREE.ShaderMaterial({
                    vertexShader,
                    fragmentShader,
                    uniforms: {
                        time: { value: 0 }
                    },
                    transparent: true,
                    depthWrite: false,
                    blending: THREE.AdditiveBlending
                });

                this.particles = new THREE.Points(geometry, this.material);
                this.scene.add(this.particles);

                this.animate();
            }

            animate() {
                if (!this.renderer || !this.material) return;
                this.animationId = requestAnimationFrame(() => this.animate());

                this.time += 0.016;
                this.material.uniforms.time.value = this.time;

                this.renderer.render(this.scene, this.camera);
            }

            onResize() {
                if (!this.camera || !this.renderer) return;
                const width = window.innerWidth;
                const height = window.innerHeight;
                this.camera.aspect = width / height;
                this.camera.updateProjectionMatrix();
                this.renderer.setSize(width, height);
            }

            dispose() {
                if (this.animationId) cancelAnimationFrame(this.animationId);
                if (this.renderer) {
                    this.renderer.dispose();
                    this.renderer.forceContextLoss();
                }
                if (this.material) this.material.dispose();
                if (this.particles?.geometry) this.particles.geometry.dispose();
            }
        }

        try {
            particleSystemRef.current = new ParticleSystem(canvasContainerRef.current);
            setRendererError(null);
        } catch (e) {
            setRendererError(`Failed to init particle system: ${e.message}`);
        }

        const handleResize = () => {
            particleSystemRef.current?.onResize();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            particleSystemRef.current?.dispose();
        };
    }, []);

    return (
        <>
            <div
                className={`hp-deep-space ${className}`}
                aria-hidden="true"
                style={{
                    background: 'radial-gradient(circle at top right, rgba(76, 29, 149, 0.15) 0%, transparent 40%), radial-gradient(circle at bottom left, rgba(49, 46, 129, 0.15) 0%, transparent 40%), #000000'
                }}
            >
                <div ref={canvasContainerRef} className="hp-deep-space-canvas" />
            </div>
            {showRendererError && rendererError && (
                <div className="hp-renderer-error">
                    WebGL Blocked: {rendererError}
                </div>
            )}
        </>
    );
};

export default DeepSpaceBackground;
